import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getUnreadCount,
  getNotificationFeed,
  markNotificationRead,
  markAllNotificationsRead,
} from '../services/notificationApi';

const POLL_INTERVAL_MS = 30000; // matches backend comment: "polled every ~30s"

const BellIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
    <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"/>
    <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
  </svg>
);

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

const NotificationBell = () => {
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    const h = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const refreshCount = useCallback(() => {
    getUnreadCount()
      .then(setUnreadCount)
      .catch(() => { /* silent — badge just won't update this cycle */ });
  }, []);

  // Poll unread count regardless of dropdown state
  useEffect(() => {
    refreshCount();
    const id = setInterval(refreshCount, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [refreshCount]);

  const loadFeed = () => {
    setLoading(true);
    getNotificationFeed(0, 10)
      .then((data) => {
        setNotifications(data.notifications || []);
        setUnreadCount(data.unreadCount ?? 0);
      })
      .catch(() => setNotifications([]))
      .finally(() => setLoading(false));
  };

  const toggleOpen = () => {
    const next = !open;
    setOpen(next);
    if (next) loadFeed();
  };

  const handleItemClick = async (n) => {
    setOpen(false);
    if (!n.isRead) {
      // Optimistic UI update
      setUnreadCount((c) => Math.max(0, c - 1));
      try { await markNotificationRead(n.id); } catch { /* best-effort */ }
    }
    if (n.actionUrl) {
      // actionUrl is a plain app path; the tenant is fixed by the subdomain
      navigate(n.actionUrl);
    }
  };

  const handleMarkAllRead = async (e) => {
    e.stopPropagation();
    setNotifications((list) => list.map((n) => ({ ...n, isRead: true })));
    setUnreadCount(0);
    try { await markAllNotificationsRead(); } catch { /* best-effort */ }
  };

  return (
    <div ref={ref} className="rel">
      <button className="btn-icon" onClick={toggleOpen} aria-label="Notifications">
        <BellIcon />
        {unreadCount > 0 && (
          <span className="cart-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
        )}
      </button>

      {open && (
        <div className="dropdown dropdown-right notif-dropdown">
          <div className="dropdown-header notif-dropdown-header">
            <span>Notifications</span>
            {unreadCount > 0 && (
              <button className="notif-mark-all" onClick={handleMarkAllRead}>
                Mark all read
              </button>
            )}
          </div>

          <div className="notif-list">
            {loading && (
              <div className="notif-empty">Loading…</div>
            )}
            {!loading && notifications.length === 0 && (
              <div className="notif-empty">You're all caught up</div>
            )}
            {!loading && notifications.map((n) => (
              <button
                key={n.id}
                className={`dropdown-item notif-item ${n.isRead ? '' : 'notif-item-unread'}`}
                onClick={() => handleItemClick(n)}
              >
                <div className="notif-item-dot" style={{ visibility: n.isRead ? 'hidden' : 'visible' }} />
                <div className="notif-item-text-wrap">
                  <div className="notif-item-title">{n.title}</div>
                  <div className="notif-item-body">{n.body}</div>
                  <div className="notif-item-time">{timeAgo(n.createdAt)}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;